import { Dispatch, Action } from "redux";
import { Port, Terminal } from "app/shared/model/Port";
import { getAllPorts } from "app/shared/api/getPorts";
import { savePortWithTerminals } from "app/shared/api/savePort";

export const FETCH_PORTS = "PORT_MANAGER/FETCH_PORTS";
export const SET_PORTS   = "PORT_MANAGER/SET_PORTS";
export const UPSERT_PORT     = "PORT_MANAGER/UPSERT_PORT";
export const UPSERT_TERMINAL = "PORT_MANAGER/UPSERT_TERMINAL";

export const setPorts = (ports: Port[]) => ({ type: SET_PORTS, payload: ports });
export const upsertPort = (port: Partial<Port>) => ({ type: UPSERT_PORT, payload: port });
export const upsertTerminal = (portId: number, terminal: Partial<Terminal>) => ({
  type: UPSERT_TERMINAL,
  payload: { portId, terminal },
});

// ───────────────────────────────────────────────────────────────────────────────
// Helpers
// ───────────────────────────────────────────────────────────────────────────────

// Build payload that follows backend rules:
// - Do NOT include port.id when creating
// - Do NOT include terminal.number when creating
function buildPortPayloadForSave(port: Port) {
  // clone shallowly
  const base: any = { ...port };

  // omit id on create
  if (!base.id || base.id === 0) {
    delete base.id;
  }

  // clean terminals
  const cleanedTerminals = (port.terminals ?? []).map(t => {
    const tt: any = { ...t };
    if ("id" in tt) {
      delete tt.id;
    }

    // if number is falsy/undefined/<=0 it's a NEW terminal; omit number
    if (tt.number == null || tt.number === undefined) {
      delete tt.number;
    }
    return tt;
  });

  base.terminals = cleanedTerminals;

  return base;
}

// Sort terminals while tolerating missing numbers (new terminals last)
function sortTerminals(terminals: Terminal[] | Partial<Terminal>[]) {
  return [...terminals].sort((a: any, b: any) => {
    const aOrder = Number(a?.order ?? a?.number ?? Number.POSITIVE_INFINITY);
    const bOrder = Number(b?.order ?? b?.number ?? Number.POSITIVE_INFINITY);
    return aOrder - bOrder;
  });
}

// ───────────────────────────────────────────────────────────────────────────────
// Thunks
// ───────────────────────────────────────────────────────────────────────────────

export const fetchPorts = () => async (dispatch: Dispatch<Action>) => {
  dispatch({ type: FETCH_PORTS });
  try {
    const ports = await getAllPorts();
    dispatch(setPorts(ports || []));
  } catch (e) {
    console.warn("Failed to fetch ports", e);
    dispatch(setPorts([]));
  }
};

// Save a port (create or update)
// - If port.id is present (>0): update
// - If port.id is missing/0: create (we omit id from payload)
export const savePort = (port: Port) => async (dispatch: Dispatch<Action>) => {
  const payload = buildPortPayloadForSave(port);
  const result = await savePortWithTerminals(payload as Port);
  if (!result.ok) throw new Error(result.message || "Failed to save port");
  await (dispatch as any)(fetchPorts());
};

// Save a terminal by saving its parent port
// - If terminal.number exists (>0): update matching terminal
// - If terminal.number missing/<=0: treat as NEW (do not assign number)
export const saveTerminal = (port: Port, terminal: Terminal) => async (dispatch: Dispatch<Action>) => {
  const terminals = [...(port.terminals ?? [])];

  const isExisting =
    terminal.number != null && Number(terminal.number) > 0;

  if (isExisting) {
    // Update by number
    const idx = terminals.findIndex(t => t.number === terminal.number);
    if (idx >= 0) {
      terminals[idx] = { ...terminals[idx], ...terminal };
    } else {
      // number provided but not found – fallback to push
      terminals.push(terminal);
    }
  } else {
    // New terminal: ensure number is NOT set
    const { id, ...rest } = terminal as any;
    terminals.push(rest as Terminal);
  }

  const updatedPort: Port = { ...port, terminals: sortTerminals(terminals) as Terminal[] };
  await (dispatch as any)(savePort(updatedPort));
};
